import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import FileUploader from '../components/FileUploader';

interface KBEntry {
  pk: string;
  sk: string;
  category: string;
  content: string;
  skills_extracted: string[];
  created_at: string;
}

export default function KnowledgeBasePage() {
  const [entries, setEntries] = useState<KBEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newText, setNewText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractedDraft, setExtractedDraft] = useState<unknown>(null);

  useEffect(() => { loadEntries(); }, []);

  async function loadEntries() {
    try {
      const data = await api.get<{ items: KBEntry[] }>('/api/kb');
      setEntries(data.items);
    } catch {}
    setLoading(false);
  }

  async function handleExtract() {
    if (!newText.trim()) return;
    setExtracting(true);
    try {
      const result = await api.post('/api/kb/ingest/extract', { text: newText, source_type: 'paste' });
      setExtractedDraft(result);
    } catch (err) {
      alert((err as Error).message);
    }
    setExtracting(false);
  }

  async function handleConfirm() {
    const draft = extractedDraft as { entries: unknown[]; _signature: string };
    try {
      await api.post('/api/kb/ingest/confirm', { entries: draft.entries, _signature: draft._signature });
      setExtractedDraft(null);
      setNewText('');
      setShowAddForm(false);
      loadEntries();
    } catch (err) {
      alert((err as Error).message);
    }
  }

  const grouped = entries.reduce((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {} as Record<string, KBEntry[]>);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          + Add Material
        </button>
      </div>

      {/* Add material form */}
      {showAddForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <h2 className="font-medium text-gray-900 mb-2">Paste your material</h2>
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Paste your experience, skills, project description, personal statement, or anything else..."
            className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Material to add"
          />

          {!extractedDraft && (
            <button
              onClick={handleExtract}
              disabled={extracting || !newText.trim()}
              className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {extracting ? 'Extracting...' : 'Extract & Categorise'}
            </button>
          )}

          {!extractedDraft && (
            <FileUploader onExtracted={(result) => setExtractedDraft(result)} />
          )}

          {extractedDraft && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800 font-medium mb-3">
                {(extractedDraft as { message: string }).message}
              </p>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {((extractedDraft as { entries: { category: string; content: string; skills_extracted?: string[] }[] }).entries).map((entry, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-gray-200 p-3">
                    <span className="inline-block text-xs font-semibold uppercase bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full mb-1">
                      {entry.category.replace('_', ' ')}
                    </span>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.content}</p>
                    {entry.skills_extracted && entry.skills_extracted.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {entry.skills_extracted.map((skill) => (
                          <span key={skill} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={handleConfirm} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700">
                  Confirm & Save
                </button>
                <button onClick={() => setExtractedDraft(null)} className="text-gray-600 px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KB entries by category */}
      {loading && <p className="text-gray-500">Loading...</p>}

      {Object.entries(grouped).map(([category, items]) => (
        <section key={category} className="mb-6" aria-labelledby={`cat-${category}`}>
          <h2 id={`cat-${category}`} className="text-lg font-semibold text-gray-800 capitalize mb-2">
            {category.replace('_', ' ')} ({items.length})
          </h2>
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.sk} className="bg-white rounded-lg border border-gray-200 p-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.content}</p>
                {item.skills_extracted.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {item.skills_extracted.map((skill) => (
                      <span key={skill} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      {!loading && entries.length === 0 && (
        <p className="text-gray-500 text-center py-8">
          Your knowledge base is empty. Add material or run the Starter Interview to get started.
        </p>
      )}
    </div>
  );
}
